<script lang="ts">
  import { Usuarios, type TenantUser } from '../lib/api';
  import BrandingAdminPanel from './BrandingAdminPanel.svelte';
  import FirmasMedicosPanel from './FirmasMedicosPanel.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { effectiveOrgTipo } from '../lib/clinic-preview.svelte';
  import { session } from '../lib/auth.svelte';
  import { filterAssignableRoles } from '../lib/tenant-roles';

  type AdminTab = 'usuarios' | 'marca' | 'firmas';
  let adminTab = $state<AdminTab>('usuarios');

  const ROL_LABEL: Record<string, string> = {
    'admin-tenant': 'Administrador', 'recepcion': 'Recepción', 'medico': 'Médico',
    'enfermeria': 'Enfermería', 'farmacia': 'Farmacia', 'laboratorio': 'Laboratorio',
    'imagenologia': 'Imagenología', 'quirofano': 'Quirófano', 'caja': 'Caja',
    'tesoreria': 'Tesorería', 'bodega': 'Bodega', 'contabilidad': 'Contabilidad',
    'nomina': 'Nómina', 'mspas': 'MSPAS', 'auditoria': 'Auditoría',
    'consultorio-externo': 'Consultorio externo', 'vet-medico': 'Vet · Médico',
    'vet-recepcion': 'Vet · Recepción', 'vet-farmacia': 'Vet · Farmacia',
  };
  const label = (r: string) => {
    if (r === 'medico' && orgEfectivo === 'clinica-fisioterapia') return 'Fisioterapista';
    if (r === 'medico' && orgEfectivo === 'clinica-nutricion') return 'Nutricionista';
    if (r === 'medico' && orgEfectivo === 'clinica-psicologia') return 'Psicólogo/a';
    return ROL_LABEL[r] ?? r;
  };

  let lista = $state<TenantUser[]>([]);
  let rolesDisponibles = $state<string[]>([]);
  let cargando = $state(false);
  let error = $state<string | null>(null);
  let aviso = $state<string | null>(null);

  let nuevo = $state<{ usuario: string; nombre: string; apellido: string; email: string; roles: string[] }>(
    { usuario: '', nombre: '', apellido: '', email: '', roles: [] });
  let credencial = $state<{ usuario: string; tempPassword: string } | null>(null);

  let editandoId = $state<string | null>(null);
  let rolesEdit = $state<string[]>([]);

  const orgEfectivo = $derived(
    effectiveOrgTipo(tenantCtx.orgTipo as import('../lib/navigation').OrgTipo | null, tenantCtx.isHospitalEdition, session.value?.tenantId),
  );

  async function recargar() {
    cargando = true; error = null;
    try {
      const [u, r] = await Promise.all([Usuarios.list(), Usuarios.rolesAsignables()]);
      lista = u.users ?? [];
      rolesDisponibles = filterAssignableRoles(r.roles ?? [], orgEfectivo);
    } catch (e: any) { error = traducir(e.message); }
    cargando = false;
  }

  function traducir(msg: string): string {
    if (!msg) return 'Error';
    if (msg.includes('requiere-modo-keycloak')) return 'La gestión de usuarios requiere iniciar sesión con Keycloak.';
    if (msg.includes('rol-admin-tenant-requerido')) return 'Necesitas el rol de Administrador.';
    if (msg.includes('keycloak-admin-deshabilitado')) return 'El provisioning de Keycloak está deshabilitado.';
    if (msg.includes('usuario-ya-existe')) return 'Ya existe un usuario con ese identificador.';
    if (msg.includes('usuario-de-otro-tenant')) return 'No puedes gestionar usuarios de otra organización.';
    return msg;
  }

  function toggleRol(arr: string[], r: string): string[] {
    return arr.includes(r) ? arr.filter(x => x !== r) : [...arr, r];
  }

  async function crear() {
    error = null; aviso = null; credencial = null;
    if (!nuevo.usuario.trim()) { error = 'El identificador de usuario es obligatorio.'; return; }
    try {
      const r = await Usuarios.crear({
        usuario: nuevo.usuario.trim(), nombre: nuevo.nombre.trim(),
        apellido: nuevo.apellido.trim(), email: nuevo.email.trim(), roles: nuevo.roles,
      });
      credencial = { usuario: r.usuario, tempPassword: r.tempPassword };
      nuevo = { usuario: '', nombre: '', apellido: '', email: '', roles: [] };
      await recargar();
    } catch (e: any) { error = traducir(e.message); }
  }

  function empezarEdicion(u: TenantUser) { editandoId = u.id; rolesEdit = [...u.roles]; }
  async function guardarRoles(u: TenantUser) {
    error = null;
    try { await Usuarios.setRoles(u.id, rolesEdit); editandoId = null; await recargar(); }
    catch (e: any) { error = traducir(e.message); }
  }
  async function toggleEnabled(u: TenantUser) {
    error = null;
    try { await Usuarios.setEnabled(u.id, !u.enabled); await recargar(); }
    catch (e: any) { error = traducir(e.message); }
  }
  async function resetPwd(u: TenantUser) {
    error = null; aviso = null; credencial = null;
    try {
      const r = await Usuarios.resetPassword(u.id);
      credencial = { usuario: u.username, tempPassword: r.tempPassword };
    } catch (e: any) { error = traducir(e.message); }
  }

  async function copiar(txt: string) {
    try { await navigator.clipboard.writeText(txt); aviso = 'Copiado al portapapeles.'; }
    catch { aviso = null; }
  }

  $effect(() => { if (adminTab === 'usuarios') recargar(); });
</script>

<div class="admin-page">
  <!-- ===== HEADER ===== -->
  <div class="page-header">
    <div class="header-icon">👥</div>
    <div class="header-info">
      <h1>Administración</h1>
      <p>Gestión de personal, identidad visual y firmas médicas</p>
    </div>
  </div>

  <!-- ===== TABS ===== -->
  <div class="tabs-bar">
    <button class="tab-btn" class:active={adminTab === 'usuarios'} onclick={() => adminTab = 'usuarios'}>
      👥 Usuarios y roles
    </button>
    <button class="tab-btn" class:active={adminTab === 'marca'} onclick={() => adminTab = 'marca'}>
      🎨 Identidad visual
    </button>
    <button class="tab-btn" class:active={adminTab === 'firmas'} onclick={() => adminTab = 'firmas'}>
      ✍️ Firmas médicas
    </button>
  </div>

  {#if adminTab === 'marca'}
    <BrandingAdminPanel />
  {:else if adminTab === 'firmas'}
    <FirmasMedicosPanel />
  {:else}
    <!-- ===== USUARIOS CONTENT ===== -->
    {#if error}<div class="msg msg-error">{error}</div>{/if}
    {#if aviso}<div class="msg msg-ok">{aviso}</div>{/if}

    {#if credencial}
      <div class="cred-card">
        <div class="cred-header">🔑 Credencial generada (se muestra una sola vez)</div>
        <div class="cred-body">
          <div class="cred-row">
            <span>Usuario:</span>
            <code>{credencial.usuario}</code>
            <button class="btn-sm" onclick={() => copiar(credencial!.usuario)}>📋 Copiar</button>
          </div>
          <div class="cred-row">
            <span>Contraseña:</span>
            <code class="pass">{credencial.tempPassword}</code>
            <button class="btn-sm" onclick={() => copiar(credencial!.tempPassword)}>📋 Copiar</button>
          </div>
        </div>
        <button class="btn-primary btn-sm" style="margin-top:0.5rem" onclick={() => credencial = null}>Entendido</button>
      </div>
    {/if}

    <!-- Nuevo usuario -->
    <div class="section-card">
      <div class="section-head">➕ Nuevo usuario</div>
      <div class="section-body">
        <div class="form-row">
          <input placeholder="Identificador (ej. maria.lopez)" bind:value={nuevo.usuario}/>
          <input placeholder="Nombre" bind:value={nuevo.nombre}/>
          <input placeholder="Apellido" bind:value={nuevo.apellido}/>
          <input placeholder="Email (opcional)" bind:value={nuevo.email}/>
        </div>
        <div class="roles-grid">
          {#each rolesDisponibles as r (r)}
            <label class="role-chip" class:on={nuevo.roles.includes(r)}>
              <input type="checkbox" checked={nuevo.roles.includes(r)}
                     onchange={() => nuevo.roles = toggleRol(nuevo.roles, r)}/>
              {label(r)}
            </label>
          {/each}
        </div>
        <button class="btn-primary" onclick={crear}>Crear usuario</button>
      </div>
    </div>

    <!-- Lista de personal -->
    <div class="section-card">
      <div class="section-head">
        <span>👥 Personal ({lista.length})</span>
        <button class="btn-sm" onclick={recargar} disabled={cargando}>🔄 Recargar</button>
      </div>
      <div class="section-body nopad">
        <table>
          <thead><tr><th>Usuario</th><th>Nombre</th><th>Roles</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>
            {#each lista as u (u.id)}
              <tr class:inactivo={!u.enabled}>
                <td><code>{u.username}</code></td>
                <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                <td>
                  {#if editandoId === u.id}
                    <div class="roles-grid small">
                      {#each rolesDisponibles as r (r)}
                        <label class="role-chip" class:on={rolesEdit.includes(r)}>
                          <input type="checkbox" checked={rolesEdit.includes(r)}
                                 onchange={() => rolesEdit = toggleRol(rolesEdit, r)}/>
                          {label(r)}
                        </label>
                      {/each}
                    </div>
                  {:else}
                    {#if u.roles.length}
                      {#each u.roles as r}<span class="role-tag">{label(r)}</span>{/each}
                    {:else}<span class="text-muted">sin roles</span>{/if}
                  {/if}
                </td>
                <td>
                  <span class="status-dot" class:on={u.enabled}></span>
                  {u.enabled ? 'Activo' : 'Inactivo'}
                </td>
                <td class="actions">
                  {#if editandoId === u.id}
                    <button class="btn-primary btn-sm" onclick={() => guardarRoles(u)}>Guardar</button>
                    <button class="btn-sm" onclick={() => editandoId = null}>Cancelar</button>
                  {:else}
                    <button class="btn-sm" onclick={() => empezarEdicion(u)}>Roles</button>
                    <button class="btn-sm" onclick={() => toggleEnabled(u)}>{u.enabled ? 'Desactivar' : 'Activar'}</button>
                    <button class="btn-sm" onclick={() => resetPwd(u)}>Reset clave</button>
                  {/if}
                </td>
              </tr>
            {/each}
            {#if lista.length === 0 && !cargando}
              <tr><td colspan="5" class="text-muted" style="text-align:center;padding:2rem">Sin usuarios todavía.</td></tr>
            {/if}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<style>
  .admin-page { display: flex; flex-direction: column; gap: 0.85rem; }

  /* ===== HEADER ===== */
  .page-header {
    display: flex; align-items: center; gap: 1rem; padding: 1.5rem 1.75rem;
    background: linear-gradient(135deg, #0d9488, #0f766e, #0c4a6e); background-size: 200% 200%;
    animation: hShift 8s ease infinite; border-radius: var(--radius-xl); color: #fff;
  }
  .header-icon { font-size: 2.2rem; width: 56px; height: 56px; display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.15); border-radius: 14px; flex-shrink: 0; }
  .header-info h1 { margin: 0; font-size: 1.35rem; font-weight: 800; color: #fff !important; }
  .header-info p { margin: 0.2rem 0 0; font-size: 0.82rem; color: rgba(255,255,255,0.7); }
  @keyframes hShift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }

  /* ===== TABS ===== */
  .tabs-bar { display: flex; gap: 0.4rem; flex-wrap: wrap; }
  .tab-btn {
    padding: 0.5rem 1rem; border-radius: 999px; border: 1px solid var(--glass-border);
    background: var(--glass-bg-card); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
    color: var(--text-secondary); cursor: pointer; font: inherit; font-size: 0.85rem; font-weight: 600;
    transition: all 0.2s;
  }
  .tab-btn:hover { border-color: var(--brand-strong); color: var(--brand-deep); }
  .tab-btn.active { background: #0d9488; color: #fff; border-color: #0d9488; box-shadow: 0 4px 14px rgba(13,148,136,0.3); }

  /* ===== MESSAGES ===== */
  .msg { padding: 0.65rem 1rem; border-radius: 0.75rem; font-size: 0.85rem; font-weight: 600; }
  .msg-error { background: rgba(239,68,68,0.1); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
  .msg-ok { background: rgba(16,185,129,0.1); color: #059669; border: 1px solid rgba(16,185,129,0.2); }

  /* ===== CREDENCIAL CARD ===== */
  .cred-card {
    background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.25);
    border-radius: var(--radius-xl); padding: 1rem 1.15rem;
    backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  }
  .cred-header { font-weight: 700; font-size: 0.9rem; color: #b45309; margin-bottom: 0.5rem; }
  .cred-body { display: flex; flex-direction: column; gap: 0.35rem; }
  .cred-row { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; flex-wrap: wrap; }
  .cred-row span { min-width: 100px; color: var(--text-secondary); font-weight: 600; }
  .cred-row code { background: rgba(255,255,255,0.6); padding: 0.2rem 0.55rem; border-radius: 0.35rem; font-size: 0.9rem; font-weight: 700; }
  .cred-row code.pass { letter-spacing: 0.04em; }

  /* ===== SECTION CARDS ===== */
  .section-card {
    background: var(--glass-bg-card); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border); border-radius: var(--radius-xl); overflow: hidden; box-shadow: var(--glass-shadow);
  }
  .section-head { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.1rem;
    background: rgba(13,148,136,0.04); border-bottom: 1px solid var(--glass-border);
    font-weight: 700; font-size: 0.88rem; color: var(--text-primary); }
  .section-body { padding: 1.1rem; }
  .section-body.nopad { padding: 0; }

  /* ===== FORM ===== */
  .form-row { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
  .form-row input { flex: 1; min-width: 180px; }

  /* ===== ROLES ===== */
  .roles-grid { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 0.85rem; }
  .roles-grid.small { margin: 0; }
  .role-chip {
    display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.3rem 0.75rem; border-radius: 999px;
    border: 1px solid var(--glass-border); background: var(--glass-bg-card); backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px); font-size: 0.8rem; cursor: pointer; color: var(--text-secondary);
    font-weight: 600; transition: all 0.2s;
  }
  .role-chip:hover { border-color: var(--brand-strong); }
  .role-chip.on { background: #0d9488; border-color: #0d9488; color: #fff; box-shadow: 0 2px 10px rgba(13,148,136,0.3); }
  .role-chip input { display: none; }
  .role-tag {
    display: inline-block; padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700;
    background: rgba(13,148,136,0.08); color: #0f766e; border: 1px solid rgba(13,148,136,0.15);
    margin: 0.1rem 0.15rem 0.1rem 0;
  }

  /* ===== TABLE ===== */
  table { width: 100%; border-collapse: collapse; }
  th { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-secondary);
    padding: 0.55rem 0.75rem; background: rgba(13,148,136,0.04); font-weight: 700;
    border-bottom: 1px solid var(--glass-border); text-align: left; }
  td { padding: 0.55rem 0.75rem; border-top: 1px solid rgba(15,23,42,0.04); color: var(--text-primary); font-size: 0.84rem; }
  tr:hover td { background: rgba(255,255,255,0.4); }
  tr.inactivo td { color: var(--text-muted); }
  code { font-family: ui-monospace, monospace; font-size: 0.82rem; }

  .status-dot { display: inline-block; width: 0.5rem; height: 0.5rem; border-radius: 50%; margin-right: 0.4rem;
    background: #9ca3af; vertical-align: middle; }
  .status-dot.on { background: #10b981; box-shadow: 0 0 6px rgba(16,185,129,0.4); }

  .actions { display: flex; gap: 0.35rem; flex-wrap: wrap; }
  .text-muted { color: var(--text-muted); }

  /* ===== BUTTONS ===== */
  .btn-primary { background: #0d9488; border: 0; color: #fff; font-weight: 700; padding: 0.5rem 1.1rem;
    border-radius: 0.65rem; cursor: pointer; font: inherit; font-size: 0.85rem;
    box-shadow: 0 4px 14px rgba(13,148,136,0.3); transition: all 0.2s; }
  .btn-primary:hover { background: #0f766e; box-shadow: 0 6px 20px rgba(13,148,136,0.4); transform: translateY(-1px); }

  .btn-sm { padding: 0.3rem 0.7rem; border-radius: 0.55rem; border: 1px solid var(--glass-border);
    background: var(--glass-bg-card); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    color: var(--text-secondary); cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 600;
    transition: all 0.2s; }
  .btn-sm:hover { background: var(--brand-surface); border-color: var(--brand-strong); color: var(--brand-deep); }
</style>
